import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import SaveBlock from 'widgets/saveBlock';
import each from 'lodash-es/each';
import { UploadWatcher } from 'shared/lib/extendedComponents/uploadWatcher/uploadWatcher';
import { InitialPlacement, PlacementParams } from 'entities/types';
import { AxiosResponse } from 'axios';
import { CreateFileParams } from 'entities/file/model/fileModel';
import { useNavigate } from 'react-router-dom';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { UploadedFileWithPreview } from 'pages/uploadPage/uploadFiles/uploadFiles';
import WatchUploadProgressModal from 'shared/lib/extendedComponents/uploadWatcher/watchUploadProgressModal';

type CreateFileFuncSignature = (
  params: CreateFileParams,
  onProgress: (val: number) => void
) => readonly [AbortController, Promise<AxiosResponse<void, any>>];

export default observer(function CreateFileController({
  getFiles,
  onFileSuccess,
  onFileError,
  isOnlyModal,
  onUpload,
  saveBlockProps,
  uploadModalText,
}: {
  getFiles:
    | (() => UploadedFileWithPreview[])
    | (() => Promise<UploadedFileWithPreview[]>);
} & {
  onFileSuccess: (name: string) => void;
  onFileError: (name: string, error: string) => void;
  isOnlyModal?: boolean;
  onUpload: CreateFileFuncSignature;
  uploadModalText: string;
  saveBlockProps: {
    onCancel: () => void;
  } & InitialPlacement;
}) {
  const [watchers, setWatchers] = useState<UploadWatcher[] | null>(null);
  const [uploadParams, setUploadParams] = useState<null | PlacementParams>(
    null
  );

  const navigate = useNavigate();
  const { getSectionSearchParams, getFolderSearchParams } =
    useSearchParamsUtils();

  return (
    <>
      {isOnlyModal ? null : (
        <SaveBlock
          shouldDropNameAfterSave
          {...saveBlockProps}
          onSubmit={async (params) => {
            setUploadParams(params);
            const _uploadedFiles: UploadWatcher[] = [];

            const files = await getFiles();

            each(files, (file, i) => {
              _uploadedFiles.push(
                new UploadWatcher({
                  name: file.originalFile.name,
                  onSuccess: onFileSuccess,
                  onError: onFileError,
                  startUpload: (onProgress) => {
                    return onUpload(
                      {
                        file: file.originalFile,
                        previewFile: file.previewFile?.originalFile,
                        section_id: params.sectionId,
                        folder_id: params.folderId,
                      },
                      onProgress
                    );
                  },
                })
              );
            });

            setWatchers(_uploadedFiles);
          }}
        ></SaveBlock>
      )}

      {watchers && uploadParams && (
        <WatchUploadProgressModal
          headerText={uploadModalText}
          isOpen={true}
          items={watchers}
          navigateToUploaded={() => {
            if (uploadParams.folderId) {
              navigate({
                pathname: '/explorer',
                search: getFolderSearchParams({
                  folderId: uploadParams.folderId,
                }),
              });
            } else {
              navigate({
                pathname: '/explorer',
                search: getSectionSearchParams({
                  sectionId: uploadParams.sectionId,
                }),
              });
            }
          }}
          handleClose={() => {
            setWatchers(null);
          }}
        ></WatchUploadProgressModal>
      )}
    </>
  );
});
